import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import {
	WithStyles,
	Theme,
	createStyles,
	withStyles,
	ListItemSecondaryAction
} from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";

import { MessageSignature } from "../../api/settings";

interface State {
	adding: boolean;
	editing: boolean;
	deleting: boolean;
	dialogID: string;
	dialogName: string;
	dialogContent: string;
}

export interface SignatureListProps {
	signatures: MessageSignature[];
	onAdd: (signature: MessageSignature) => void;
	onEdit: (signature: MessageSignature) => void;
	onDelete: (id: string) => void;
}

interface ConnectedSignatureListProps extends WithStyles<typeof styles> {}

type Props = SignatureListProps &
	ConnectedSignatureListProps &
	InjectedTranslateProps;

export class SignatureList extends React.Component<Props, State> {
	public state: State = {
		adding: false,
		editing: false,
		deleting: false,
		dialogID: "",
		dialogName: "",
		dialogContent: ""
	};

	private getSignatureById = (id: string) => {
		return this.props.signatures.find(s => s.id === id);
	};

	private handleAddClick = () => {
		this.setState({
			adding: true,
			dialogID: "",
			dialogName: "",
			dialogContent: ""
		});
	};

	private handleEditClick = (id: string) => {
		const signature = this.getSignatureById(id);

		this.setState({
			editing: true,
			dialogID: id,
			dialogName: signature.name,
			dialogContent: signature.content
		});
	};

	private handleDeleteClick = (id: string) => {
		const signature = this.getSignatureById(id);

		this.setState({
			deleting: true,
			dialogID: id,
			dialogName: signature.name,
			dialogContent: ""
		});
	};

	private handleDialogCancelClick = () => {
		this.setState({
			adding: false,
			editing: false,
			deleting: false
		});
	};

	private handleDialogNameChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		this.setState({
			dialogName: event.currentTarget.value
		});
	};

	private handleDialogContentChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		this.setState({
			dialogContent: event.currentTarget.value
		});
	};

	private handleAddSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		this.props.onAdd({
			id: null,
			name: this.state.dialogName,
			content: this.state.dialogContent
		});

		this.handleDialogCancelClick();
	};

	private handleEditSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		this.props.onEdit({
			id: this.state.dialogID,
			name: this.state.dialogName,
			content: this.state.dialogContent
		});

		this.handleDialogCancelClick();
	};

	private handleDeleteSubmitClick = () => {
		this.props.onDelete(this.state.dialogID);

		this.handleDialogCancelClick();
	};

	private renderSignatureList() {
		const { classes, t, signatures } = this.props;
		const {
			adding,
			editing,
			deleting,
			dialogName,
			dialogContent
		} = this.state;

		if (signatures.length === 0) {
			return (
				<ListItem>
					<ListItemText className={classes.notAvailableItem}>
						Noch keine Signaturen vorhanden
					</ListItemText>
				</ListItem>
			);
		}

		return signatures.map(signature => (
			<ListItem key={signature.id}>
				<ListItemText>{signature.name}</ListItemText>
				<ListItemSecondaryAction>
					<IconButton
						onClick={() => this.handleEditClick(signature.id)}
					>
						<EditIcon />
					</IconButton>
					<IconButton
						onClick={() => this.handleDeleteClick(signature.id)}
					>
						<DeleteIcon />
					</IconButton>
				</ListItemSecondaryAction>
			</ListItem>
		));
	}

	public render() {
		const { classes, t, signatures } = this.props;
		const {
			adding,
			editing,
			deleting,
			dialogName,
			dialogContent
		} = this.state;

		return (
			<Paper>
				<List>{this.renderSignatureList()}</List>
				<div className={classes.buttonContainer}>
					<Button onClick={this.handleAddClick} variant="outlined">
						Hinzufügen
					</Button>
				</div>
				<Dialog open={adding}>
					<form onSubmit={this.handleAddSubmit}>
						<DialogTitle>Neue Signatur hinzufügen</DialogTitle>
						<DialogContent>
							<TextField
								label="Name"
								name="name"
								type="text"
								value={dialogName}
								onChange={this.handleDialogNameChange}
								fullWidth
								required
								autoComplete="off"
							/>
							<TextField
								className={classes.textarea}
								label="Inhalt"
								name="content"
								type="text"
								multiline
								rows={5}
								fullWidth
								required
								autoComplete="off"
								value={dialogContent}
								onChange={this.handleDialogContentChange}
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={this.handleDialogCancelClick}>
								Abbrechen
							</Button>
							<Button type="submit" color="primary">
								Hinzufügen
							</Button>
						</DialogActions>
					</form>
				</Dialog>
				<Dialog open={editing}>
					<form onSubmit={this.handleEditSubmit}>
						<DialogTitle>Signatur bearbeiten</DialogTitle>
						<DialogContent>
							<TextField
								label="Name"
								type="text"
								value={dialogName}
								onChange={this.handleDialogNameChange}
								fullWidth
								required
								autoComplete="off"
							/>
							<TextField
								className={classes.textarea}
								label="Inhalt"
								type="text"
								multiline
								rows={5}
								fullWidth
								required
								autoComplete="off"
								value={dialogContent}
								onChange={this.handleDialogContentChange}
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={this.handleDialogCancelClick}>
								Abbrechen
							</Button>
							<Button type="submit" color="primary">
								Speichern
							</Button>
						</DialogActions>
					</form>
				</Dialog>
				<Dialog open={deleting}>
					<DialogTitle>Signatur löschen</DialogTitle>
					<DialogContent>
						<DialogContentText>
							Signatur <strong>{dialogName}</strong> wirklich
							löschen?
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={this.handleDialogCancelClick}>
							Abbrechen
						</Button>
						<Button
							onClick={this.handleDeleteSubmitClick}
							color="primary"
						>
							Löschen
						</Button>
					</DialogActions>
				</Dialog>
			</Paper>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({});

const mapDispatchToProps = (dispatch: Dispatch) => ({});

const styles = (theme: Theme) =>
	createStyles({
		buttonContainer: {
			display: "flex",
			justifyContent: "flex-end",
			padding: 20
		},
		textarea: {
			marginTop: 20
		},
		notAvailableItem: {
			color: "#999",
			fontStyle: "italic"
		}
	});

export const ConnectedSignatureList = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(SignatureList)
	)
);
